import React, { useState, useRef, useEffect } from 'react';
import { TextArea, Flex, View } from '@adobe/react-spectrum';
import { useDispatch, useSelector } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
// import Checkmark from '@spectrum-icons/workflow/Checkmark';
import styles from './TimelineCard.module.scss';
import { SaveButton } from './SaveButton';
import {
  selectTooltipDetails,
  selectUpdateSuccessAlert
} from 'app/pages/Home/slice/selectors';

interface EditReportingSummaryProps {
  reportingSummary: string;
  lastUpdated?: string;
  isDateClicked: boolean;
  canSeeTooltipHover: boolean;
  className?: string;
  wholeCard: any;
  setIsDateClicked: (isDateClicked: boolean) => void;
  refreshSource?: 'reviewApproval';
}

export const EditReportingSummary: React.FC<EditReportingSummaryProps> = ({
  reportingSummary,
  lastUpdated,
  isDateClicked,
  canSeeTooltipHover,
  wholeCard,
  className = '',
  setIsDateClicked,
  refreshSource
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(reportingSummary);
  const [textAreaElement, setTextAreaElement] =
    useState<HTMLTextAreaElement | null>(null);
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const tooltipDetails = useSelector(selectTooltipDetails);
  const updateSuccessAlert = useSelector(selectUpdateSuccessAlert);
  const processedAlertRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean the summary by removing HTML tags (for character count and editing)
  const cleanSummary = editValue.replace(/<[^>]*>/g, '');
  const isOverLimit = cleanSummary.length >= 500;

  // For display mode: use original HTML if within limit, otherwise truncate the clean version
  const displaySummaryHtml =
    cleanSummary.length > 500
      ? cleanSummary.substring(0, 500) + '...'
      : editValue; // Use original HTML for display

  // Check if any tooltip details have been modified
  const hasChanges = Boolean(
    (tooltipDetails.title &&
      tooltipDetails.title.trim() !== '' &&
      tooltipDetails.title !== wholeCard?.title) ||
      (tooltipDetails.riskLevel &&
        tooltipDetails.riskLevel.trim() !== '' &&
        tooltipDetails.riskLevel !== wholeCard?.riskLevel) ||
      (tooltipDetails.releaseDate &&
        tooltipDetails.releaseDate.trim() !== '') ||
      (tooltipDetails.effectiveDate &&
        tooltipDetails.effectiveDate.trim() !== '') ||
      (tooltipDetails.reportingSummary !== undefined &&
        tooltipDetails.reportingSummary !==
          reportingSummary.replace(/<[^>]*>/g, ''))
  );

  const handleSummaryClick = () => {
    if (isDateClicked) {
      setIsEditing(true);
    }
  };

  useEffect(() => {
    setEditValue(reportingSummary);
  }, [reportingSummary, isDateClicked]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset editing state when tooltip is closed (isDateClicked becomes false)
  useEffect(() => {
    if (!isDateClicked) {
      setIsEditing(false);
    }
  }, [isDateClicked]);

  // Auto focus and position cursor at end when editing starts
  useEffect(() => {
    if (isEditing && textAreaElement) {
      textAreaElement.focus();
      const length = textAreaElement.value.length;
      textAreaElement.setSelectionRange(length, length);
    }
  }, [isEditing, textAreaElement]);

  // Function to click date element with retry
  const clickDateElementWithRetry = (dateElement: HTMLElement | null) => {
    if (dateElement) {
      dateElement.click(); //don't open popup after afdate
      setTimeout(() => {
        if (dateElement) {
          dateElement.click();
        }
      }, 600);
    }
  };

  // Scroll to card after successful update
  useEffect(() => {
    let scrollTimeoutId: NodeJS.Timeout | null = null;
    let clickTimeoutId: NodeJS.Timeout | null = null;

    if (
      updateSuccessAlert &&
      updateSuccessAlert.issueId === wholeCard?.issueId
    ) {
      // Create unique identifier for this alert
      const alertId = `${updateSuccessAlert.issueId}-${
        updateSuccessAlert.releasePhaseId || ''
      }`;

      // Check if we've already processed this alert
      if (processedAlertRef.current === alertId) {
        return;
      }

      // Mark this alert as processed
      processedAlertRef.current = alertId;

      // Wait for DOM to update after data reload, then scroll
      scrollTimeoutId = setTimeout(() => {
        let cardElement: Element | null = null;
        if (updateSuccessAlert?.releasePhaseId) {
          cardElement = document.querySelector(
            `[data-unique-cardId="timeline-card-${updateSuccessAlert.issueId}-${
              updateSuccessAlert?.releasePhaseId
                ? updateSuccessAlert?.releasePhaseId
                : ''
            }"]`
          );
        } else {
          cardElement = document.querySelector(
            `[data-unique-cardId="timeline-card-${updateSuccessAlert.issueId}-"]`
          );
        }

        if (cardElement) {
          cardElement.scrollIntoView({
            // behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });

          // After scrolling, click the date container to open tooltip
          clickTimeoutId = setTimeout(() => {
            let dateElement: HTMLElement | null = null;
            if (updateSuccessAlert?.releasePhaseId) {
              dateElement = document.querySelector(
                `[data-unique-dateContainerId="timeline-card-date-${updateSuccessAlert.issueId}-${updateSuccessAlert.releasePhaseId}"]`
              );
            } else {
              dateElement = document.querySelector(
                `[data-unique-dateContainerId="timeline-card-date-${updateSuccessAlert.issueId}-"]`
              );
            }
            clickDateElementWithRetry(dateElement);
          }, 800); // Wait for scroll to complete
        }
        // Don't clear the alert - Home component handles it with 10s timer
      }, 900);
    } else if (!updateSuccessAlert) {
      // Reset the ref when alert is cleared
      processedAlertRef.current = null;
    }

    // Cleanup function to clear timeouts
    return () => {
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      if (clickTimeoutId) {
        clearTimeout(clickTimeoutId);
      }
    };
  }, [updateSuccessAlert, wholeCard?.issueId]);

  const handleSummarySave = () => {
    // Execute any pending dispatch immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      if (editValue !== reportingSummary) {
        dispatch(
          actions.updateTooltipDetails({
            reportingSummary: editValue
          })
        );
      }
    }

    setIsEditing(false);
  };

  const handleSave = () => {
    // ✅ FIX: Close tooltip immediately when Save is clicked
    setIsDateClicked(false);

    // Reset the processed alert ref to allow re-triggering for the same card
    processedAlertRef.current = null;

    const summary = reportingSummary !== editValue ? editValue : undefined;
    const fieldsToUpdate = {
      title: tooltipDetails.title,
      riskLevel: tooltipDetails.riskLevel,
      releaseDate: tooltipDetails.releaseDate,
      releaseDateKey: tooltipDetails.releaseDateKey,
      effectiveDate: tooltipDetails.effectiveDate,
      effectiveDateKey: tooltipDetails.effectiveDateKey,
      reportingSummary: summary
    };

    // ✅ FIX: Reset tooltip details immediately to close the tooltip
    dispatch(actions.resetTooltipDetails());

    dispatch(
      actions.updateMomentDetails({
        issueId: wholeCard?.issueId,
        momentType: wholeCard?.projectType,
        releasePhaseId: wholeCard?.releasePhaseId,
        refreshSource,
        fields: fieldsToUpdate
      })
    );

    dispatch(actions.resetTooltipDetails());
  };

  const handleSummaryCancel = () => {
    // Cancel any pending dispatch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setEditValue(reportingSummary);
    setIsEditing(false);
  };

  const handleTextChange = (value: string) => {
    if (value.length <= 500) {
      setEditValue(value);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer to dispatch after 300ms
      debounceTimerRef.current = setTimeout(() => {
        if (value !== reportingSummary) {
          dispatch(
            actions.updateTooltipDetails({
              reportingSummary: value
            })
          );
        }
      }, 150);
    }
  };

  const handleTextAreaRef = (el: any) => {
    if (el) {
      // Find the actual textarea element within the React Spectrum component
      const domNode = el.UNSAFE_getDOMNode();
      if (domNode) {
        const textarea = domNode.querySelector('textarea');
        if (textarea) {
          setTextAreaElement(textarea);
        }
      }
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the focus is moving to the save button
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (
      relatedTarget &&
      relatedTarget.getAttribute('aria-label') === 'Save reporting summary'
    ) {
      return; // Don't cancel if focus is moving to save button
    }
    // handleSummaryCancel();
    handleSummarySave();
  };

  return (
    <Flex
      UNSAFE_className={styles.tooltipRow}
      gap="size-75"
      direction="column"
      alignItems="start"
      width="100%"
    >
      {isDateClicked && isEditing ? (
        <>
          <TextArea
            ref={handleTextAreaRef}
            value={cleanSummary}
            onChange={handleTextChange}
            onBlur={handleBlur}
            UNSAFE_className={`${styles.reportingSummaryTextArea} ${
              isOverLimit ? styles.errorBorder : ''
            }`}
            aria-label="Edit reporting summary"
            width="100%"
            minHeight="size-400"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          {isOverLimit && (
            <div className={styles.errorMessage}>
              Maximum 500 characters allowed. Current: {cleanSummary.length}
            </div>
          )}
        </>
      ) : (
        <div
          className={`${styles.clickableReportingSummary} ${
            !isDateClicked ? styles.readOnlyReportingSummary : ''
          }`}
          onClick={handleSummaryClick}
        >
          {cleanSummary ? (
            <div
              className={`${styles.toolTipSummary} ${className}`}
              dangerouslySetInnerHTML={{ __html: displaySummaryHtml }}
            />
          ) : (
            <p className={`${styles.enterHeadline}`}>
              {isDateClicked
                ? 'Enter Headline here'
                : canSeeTooltipHover
                ? 'Click on date to enter headline, update date or status'
                : ''}
            </p>
          )}
        </div>
      )}

      {/* Save Button - Always visible, color based on edit permissions */}
      <Flex
        UNSAFE_className={[styles.tooltipRow, styles.dates].join(' ')}
        gap="size-75"
        direction="row"
        alignItems="center"
        justifyContent="end"
        width="100%"
      >
        {lastUpdated && (
          <View UNSAFE_className={styles.tooltipDate}>
            Last Updated: {lastUpdated}
          </View>
        )}
        <SaveButton hasChanges={hasChanges} onSubmit={handleSave} />
      </Flex>
    </Flex>
  );
};
